import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText } from 'lucide-react';
function LegalModal({
  isOpen,
  onClose,
  title,
  icon: Icon,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-88">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-89">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-forest-dark/80 backdrop-blur-sm" data-id="element-90" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.3
      }} onClick={e => e.stopPropagation()} className="relative z-10 bg-parchment-light rounded-2xl shadow-2xl shadow-black/30 border border-gold/15 w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" data-id="element-91">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-gold/10 bg-parchment-dark/50 shrink-0" data-id="element-92">
              <div className="flex items-center gap-3" data-id="element-93">
                <Icon className="w-5 h-5 text-gold" data-id="element-94" />
                <h2 className="font-display text-xl md:text-2xl text-forest-dark font-bold" data-id="element-95">
                  {title}
                </h2>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-brown-dark/10 transition-colors text-brown-light" data-id="element-96">
                <X className="w-5 h-5" data-id="element-97" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 md:px-8 py-6 overflow-y-auto text-brown-light leading-relaxed text-sm md:text-base space-y-5" data-id="element-98">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="bg-forest-dark border-t border-white/5 py-10" data-id="element-99">
        <div className="container mx-auto px-6 text-center" data-id="element-100">
          <h3 className="font-display text-2xl text-gold-light mb-2 opacity-80" data-id="element-101">
            Del Dolor a la Conciencia
          </h3>
          <p className="text-white text-sm mb-6" data-id="element-102">
            Las Cinco Leyes para Transformar tu Vida
          </p>
          <div className="w-12 h-[1px] bg-gold/30 mx-auto mb-6" data-id="element-103" />

          {/* Legal Links */}
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-104">
            <button onClick={() => setShowPrivacy(true)} className="flex items-center gap-1.5 text-white hover:text-gold-light text-sm transition-colors cursor-pointer" data-id="element-105">
              <Shield className="w-3.5 h-3.5" data-id="element-106" />
              Política de Privacidad
            </button>
            <span className="text-white/30" data-id="element-107">|</span>
            <button onClick={() => setShowTerms(true)} className="flex items-center gap-1.5 text-white hover:text-gold-light text-sm transition-colors cursor-pointer" data-id="element-108">
              <FileText className="w-3.5 h-3.5" data-id="element-109" />
              Términos y Condiciones
            </button>
          </div>

          <p className="text-white text-xs" data-id="element-110">
            © {new Date().getFullYear()} Rosana G. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" icon={Shield} data-id="element-111">
        <p className="font-semibold text-forest-dark" data-id="element-112">
          Última actualización: Abril 2026
        </p>

        <p data-id="element-113">
          En <strong data-id="element-114">Del Dolor a la Conciencia</strong>, nos comprometemos a
          proteger tu privacidad. Esta política describe cómo recopilamos,
          usamos y protegemos tu información personal.
        </p>

        <div data-id="element-115">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-116">
            1. Información que Recopilamos
          </h3>
          <p data-id="element-117">
            Podemos recopilar la siguiente información cuando realizas una
            compra o te suscribes a nuestro contenido:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1 ml-2" data-id="element-118">
            <li data-id="element-119">Nombre y dirección de correo electrónico</li>
            <li data-id="element-120">
              Información de pago (procesada de forma segura por terceros)
            </li>
            <li data-id="element-121">Datos de navegación y uso del sitio web</li>
          </ul>
        </div>

        <div data-id="element-122">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-123">
            2. Uso de la Información
          </h3>
          <p data-id="element-124">Utilizamos tu información para:</p>
          <ul className="list-disc list-inside mt-2 space-y-1 ml-2" data-id="element-125">
            <li data-id="element-126">Procesar y entregar tu compra</li>
            <li data-id="element-127">
              Enviarte actualizaciones relacionadas con el libro (solo si das tu
              consentimiento)
            </li>
            <li data-id="element-128">Mejorar la experiencia del usuario en nuestro sitio</li>
          </ul>
        </div>

        <div data-id="element-129">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-130">
            3. Protección de Datos
          </h3>
          <p data-id="element-131">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            alteración o destrucción.
          </p>
        </div>

        <div data-id="element-132">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-133">
            4. Cookies
          </h3>
          <p data-id="element-134">
            Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
            navegación. Puedes configurar tu navegador para rechazar cookies,
            aunque esto podría afectar la funcionalidad del sitio.
          </p>
        </div>

        <div data-id="element-135">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-136">
            5. Tus Derechos
          </h3>
          <p data-id="element-137">
            Tienes derecho a acceder, rectificar o eliminar tus datos personales
            en cualquier momento. Para ejercer estos derechos, puedes
            contactarnos a través de los canales disponibles en este sitio.
          </p>
        </div>

        <div data-id="element-138">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-139">
            6. Cambios en esta Política
          </h3>
          <p data-id="element-140">
            Nos reservamos el derecho de actualizar esta política en cualquier
            momento. Los cambios serán publicados en esta misma página con la
            fecha de actualización correspondiente.
          </p>
        </div>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" icon={FileText} data-id="element-141">
        <p className="font-semibold text-forest-dark" data-id="element-142">
          Última actualización: Abril 2026
        </p>

        <p data-id="element-143">
          Al acceder a este sitio web y/o adquirir el libro{' '}
          <strong data-id="element-144">
            "Del Dolor a la Conciencia: Las Cinco Leyes para Transformar tu
            Vida"
          </strong>
          , aceptas los siguientes términos y condiciones.
        </p>

        <div data-id="element-145">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-146">
            1. Producto y Contenido
          </h3>
          <p data-id="element-147">
            Este libro tiene fines exclusivamente educativos e informativos. El
            contenido no sustituye el consejo médico profesional, diagnóstico o
            tratamiento. Consulta siempre con un profesional de la salud
            cualificado ante cualquier duda sobre tu condición médica.
          </p>
        </div>

        <div data-id="element-148">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-149">
            2. Propiedad Intelectual
          </h3>
          <p data-id="element-150">
            Todo el contenido del libro y de este sitio web, incluyendo textos,
            imágenes, diseños y logotipos, es propiedad de Rosana G. y está
            protegido por las leyes de propiedad intelectual. Queda prohibida su
            reproducción, distribución o modificación sin autorización expresa.
          </p>
        </div>

        <div data-id="element-151">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-152">
            3. Compras y Pagos
          </h3>
          <ul className="list-disc list-inside mt-2 space-y-1 ml-2" data-id="element-153">
            <li data-id="element-154">
              Los precios están expresados en dólares estadounidenses (USD)
            </li>
            <li data-id="element-155">
              El pago se procesa a través de plataformas seguras de terceros
            </li>
            <li data-id="element-156">
              Una vez completada la compra digital, recibirás acceso inmediato
              al contenido
            </li>
          </ul>
        </div>

        <div data-id="element-157">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-158">
            4. Política de Reembolso
          </h3>
          <p data-id="element-159">
            Debido a la naturaleza digital del producto, no se ofrecen
            reembolsos una vez descargado el contenido. Si experimentas
            problemas técnicos con la descarga, contáctanos y te asistiremos
            para resolver el inconveniente.
          </p>
        </div>

        <div data-id="element-160">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-161">
            5. Limitación de Responsabilidad
          </h3>
          <p data-id="element-162">
            La autora y los distribuidores no se hacen responsables de los
            resultados individuales obtenidos a partir de la lectura del libro.
            La aplicación del contenido es responsabilidad exclusiva del lector.
          </p>
        </div>

        <div data-id="element-163">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-164">
            6. Modificaciones
          </h3>
          <p data-id="element-165">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Las modificaciones entrarán en vigor desde su publicación
            en este sitio web.
          </p>
        </div>

        <div data-id="element-166">
          <h3 className="font-display text-lg text-forest-dark font-semibold mb-2" data-id="element-167">
            7. Ley Aplicable
          </h3>
          <p data-id="element-168">
            Estos términos se rigen por las leyes vigentes en el país de
            residencia de la autora. Cualquier disputa será resuelta ante los
            tribunales competentes de dicha jurisdicción.
          </p>
        </div>
      </LegalModal>
    </>;
}